import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Trash2, X, Search, GripVertical, CheckSquare,
  AlignJustify, List, Type, Hash, Camera, Star, Calendar,
  ChevronDown, Edit2, Save, BarChart2, FileText, Copy, Eye, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';

// ─── Config des types de critères ────────────────────────────────────────────

const TYPES = [
  { value: 'checkbox',  label: 'Case à cocher',       icon: CheckSquare, color: '#15803D', bg: '#F0FDF4', extra: null },
  { value: 'niveau',    label: 'Barre de niveau',      icon: BarChart2,   color: '#1D4ED8', bg: '#EFF6FF', extra: 'niveaux' },
  { value: 'select',    label: 'Liste déroulante',     icon: List,        color: '#C2410C', bg: '#FFF7ED', extra: 'options' },
  { value: 'text',      label: 'Saisie texte',         icon: Type,        color: '#6B7280', bg: '#F9FAFB', extra: null },
  { value: 'compteur',  label: 'Compteur numérique',   icon: Hash,        color: '#7C3AED', bg: '#FAF5FF', extra: 'unite' },
  { value: 'photo',     label: 'Photo',                icon: Camera,      color: '#DB2777', bg: '#FDF2F8', extra: null },
  { value: 'note',      label: 'Note (étoiles)',       icon: Star,        color: '#D97706', bg: '#FFFBEB', extra: null },
  { value: 'date',      label: 'Date',                 icon: Calendar,    color: '#0891B2', bg: '#ECFEFF', extra: null },
];

function typeInfo(val) { return TYPES.find(t => t.value === val) || TYPES[3]; }

function TypeBadge({ type, small }) {
  const t = typeInfo(type);
  const Ic = t.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-semibold ${small ? 'text-xs px-1.5 py-0.5' : 'text-xs px-2 py-1'}`}
      style={{ background: t.bg, color: t.color }}>
      <Ic size={small ? 10 : 12} />
      {!small && t.label}
    </span>
  );
}

// ─── Formulaire de critère ────────────────────────────────────────────────────

function CritereForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || {
    nom: '', type: 'checkbox', description: '', requis: false,
    options: [], niveaux: [0, 25, 50, 75, 100], unite: '', note_max: 5,
  });
  const [optText, setOptText] = useState((initial?.options || []).join('\n'));
  const [niveauxText, setNiveauxText] = useState((initial?.niveaux || [0, 25, 50, 75, 100]).join(', '));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const curType = typeInfo(form.type);

  const handleSave = async () => {
    if (!form.nom.trim()) { toast.error('Nom obligatoire'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        nom: form.nom.trim(),
        options: form.type === 'select' ? optText.split('\n').map(s => s.trim()).filter(Boolean) : [],
        niveaux: form.type === 'niveau' ? niveauxText.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n)) : [0, 25, 50, 75, 100],
      };
      await onSave(payload);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 p-4 rounded-xl border" style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
      {/* Nom */}
      <div className="flex items-center gap-2">
        <span className="w-20 text-xs font-semibold text-right flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Nom *</span>
        <input value={form.nom} onChange={e => set('nom', e.target.value)}
          placeholder="Ex: Niveau carburant"
          className="flex-1 px-3 py-2 rounded-xl border text-sm"
          style={{ borderColor: 'var(--border)' }}
          data-testid="critere-nom-input" autoFocus />
      </div>
      {/* Type */}
      <div className="flex items-center gap-2">
        <span className="w-20 text-xs font-semibold text-right flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Type</span>
        <div className="flex flex-wrap gap-1.5 flex-1">
          {TYPES.map(t => {
            const Ic = t.icon;
            const active = form.type === t.value;
            return (
              <button key={t.value} type="button" onClick={() => set('type', t.value)}
                data-testid={`type-btn-${t.value}`}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all"
                style={{
                  background: active ? t.bg : 'white',
                  color: active ? t.color : 'var(--text-muted)',
                  borderColor: active ? t.color : 'var(--border)',
                  fontWeight: active ? 700 : 400,
                }}>
                <Ic size={12} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>
      {/* Description */}
      <div className="flex items-center gap-2">
        <span className="w-20 text-xs font-semibold text-right flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Description</span>
        <input value={form.description} onChange={e => set('description', e.target.value)}
          placeholder="Indications pour l'agent de contrôle"
          className="flex-1 px-3 py-2 rounded-xl border text-sm"
          style={{ borderColor: 'var(--border)' }} />
      </div>
      {/* Champs spécifiques au type */}
      {form.type === 'select' && (
        <div className="flex gap-2">
          <span className="w-20 text-xs font-semibold text-right flex-shrink-0 pt-1" style={{ color: 'var(--text-muted)' }}>Options</span>
          <textarea value={optText} onChange={e => setOptText(e.target.value)}
            rows={4} placeholder={"Bon état\nUsé\nEndommagé\n(une option par ligne)"}
            className="flex-1 px-3 py-2 rounded-xl border text-sm resize-none"
            style={{ borderColor: 'var(--border)' }} />
        </div>
      )}
      {form.type === 'niveau' && (
        <div className="flex items-center gap-2">
          <span className="w-20 text-xs font-semibold text-right flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Paliers (%)</span>
          <input value={niveauxText} onChange={e => setNiveauxText(e.target.value)}
            placeholder="0, 25, 50, 75, 100"
            className="flex-1 px-3 py-2 rounded-xl border text-sm"
            style={{ borderColor: 'var(--border)' }} />
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>séparés par des virgules</span>
        </div>
      )}
      {form.type === 'compteur' && (
        <div className="flex items-center gap-2">
          <span className="w-20 text-xs font-semibold text-right flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Unité</span>
          <input value={form.unite} onChange={e => set('unite', e.target.value)}
            placeholder="km, heures, litres…"
            className="w-40 px-3 py-2 rounded-xl border text-sm"
            style={{ borderColor: 'var(--border)' }} />
        </div>
      )}
      {/* Obligatoire */}
      <div className="flex items-center gap-2 pl-22">
        <span className="w-20 text-xs font-semibold text-right flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Obligatoire</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.requis} onChange={e => set('requis', e.target.checked)}
            className="w-4 h-4 rounded" style={{ accentColor: 'var(--primary-solid)' }} />
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Ce critère doit être renseigné</span>
        </label>
      </div>
      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button onClick={handleSave} disabled={saving}
          data-testid="save-critere-btn"
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: 'var(--primary-gradient)' }}>
          <Save size={13} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
        <button onClick={onCancel}
          className="px-4 py-2 rounded-xl text-sm border"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          Annuler
        </button>
      </div>
    </div>
  );
}

// ─── Panel d'édition d'un modèle (2 colonnes drag & drop) ────────────────────

function ModelePanel({ modele, criteresCatalogue, onClose, onSaved }) {
  const isNew = !modele;
  const [nom, setNom] = useState(modele?.nom || '');
  const [description, setDescription] = useState(modele?.description || '');
  const [criteres, setCriteres] = useState(modele?.criteres || []);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  // Drag from catalogue
  const [draggingCatalogueId, setDraggingCatalogueId] = useState(null);
  // Drag to reorder in model
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragModelIdx = useRef(null);

  const filteredCatalogue = criteresCatalogue.filter(c =>
    !criteres.some(mc => mc.critere_id === c.critere_id) &&
    (c.nom.toLowerCase().includes(search.toLowerCase()) || c.type.toLowerCase().includes(search.toLowerCase()))
  );

  // Add from catalogue (drag or click)
  const addCritere = (c) => {
    if (criteres.some(mc => mc.critere_id === c.critere_id)) return;
    setCriteres(prev => [...prev, { critere_id: c.critere_id, nom: c.nom, type: c.type, requis: c.requis, ordre: prev.length }]);
  };

  const removeCritere = (critere_id) => setCriteres(prev => prev.filter(c => c.critere_id !== critere_id));

  // Drag & Drop — catalogue → modèle
  const handleCatDragStart = (e, c) => {
    setDraggingCatalogueId(c.critere_id);
    e.dataTransfer.effectAllowed = 'copy';
  };
  const handleModelDrop = (e) => {
    e.preventDefault();
    if (!draggingCatalogueId) return;
    const c = criteresCatalogue.find(x => x.critere_id === draggingCatalogueId);
    if (c) addCritere(c);
    setDraggingCatalogueId(null);
    setDragOverIdx(null);
  };

  // Drag & Drop — réordonner dans le modèle
  const handleModelItemDragStart = (e, idx) => {
    dragModelIdx.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleModelItemDragOver = (e, idx) => {
    e.preventDefault();
    if (draggingCatalogueId) return; // catalogue drag en cours
    setDragOverIdx(idx);
  };
  const handleModelItemDrop = (e, toIdx) => {
    e.preventDefault();
    if (draggingCatalogueId) { handleModelDrop(e); return; }
    const fromIdx = dragModelIdx.current;
    if (fromIdx === null || fromIdx === toIdx) return;
    const next = [...criteres];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setCriteres(next.map((c, i) => ({ ...c, ordre: i })));
    dragModelIdx.current = null;
    setDragOverIdx(null);
  };

  const handleSave = async () => {
    if (!nom.trim()) { toast.error('Nom du modèle obligatoire'); return; }
    setSaving(true);
    try {
      const payload = { nom: nom.trim(), description, criteres };
      if (isNew) {
        await api.createModeleControle(payload);
        toast.success('Modèle créé');
      } else {
        await api.updateModeleControle(modele.modele_id, payload);
        toast.success('Modèle mis à jour');
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="flex-1" onClick={onClose} />
      <div className="flex flex-col bg-white shadow-2xl overflow-hidden" style={{ width: '80vw', maxWidth: 1100 }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <AlignJustify size={18} style={{ color: 'var(--primary-solid)', flexShrink: 0 }} />
            <input value={nom} onChange={e => setNom(e.target.value)}
              placeholder="Nom du modèle *"
              className="flex-1 text-lg font-semibold outline-none bg-transparent"
              style={{ color: 'var(--text)' }}
              data-testid="modele-nom-input" />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={handleSave} disabled={saving}
              data-testid="save-modele-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: 'var(--primary-gradient)' }}>
              <Save size={13} /> {saving ? 'Enregistrement...' : isNew ? 'Créer le modèle' : 'Enregistrer'}
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
              <X size={16} style={{ color: 'var(--text-muted)' }} />
            </button>
          </div>
        </div>

        {/* Description */}
        <div className="px-6 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
          <input value={description} onChange={e => setDescription(e.target.value)}
            placeholder="Description du modèle (optionnel)"
            className="w-full text-sm outline-none"
            style={{ color: 'var(--text-muted)' }} />
        </div>

        {/* Corps en 2 colonnes */}
        <div className="flex flex-1 overflow-hidden">

          {/* Col gauche — Catalogue de critères */}
          <div className="flex flex-col border-r" style={{ borderColor: 'var(--border)', width: '40%', minWidth: 280 }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
              <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>
                Catalogue ({filteredCatalogue.length})
              </p>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border" style={{ borderColor: 'var(--border)', background: 'white' }}>
                <Search size={13} style={{ color: 'var(--text-muted)' }} />
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher un critère..."
                  className="flex-1 text-sm outline-none bg-transparent"
                  style={{ color: 'var(--text)' }} />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {filteredCatalogue.length === 0 ? (
                <p className="text-xs text-center py-6" style={{ color: 'var(--text-muted)' }}>
                  {criteresCatalogue.length === 0 ? 'Aucun critère dans le catalogue' : 'Tous les critères sont ajoutés'}
                </p>
              ) : filteredCatalogue.map(c => (
                <div
                  key={c.critere_id}
                  draggable
                  onDragStart={e => handleCatDragStart(e, c)}
                  onDragEnd={() => setDraggingCatalogueId(null)}
                  data-testid={`cat-critere-${c.critere_id}`}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-grab active:cursor-grabbing transition-all hover:shadow-sm"
                  style={{
                    borderColor: 'var(--border)',
                    background: draggingCatalogueId === c.critere_id ? '#EEF2FF' : 'white',
                    opacity: draggingCatalogueId === c.critere_id ? 0.6 : 1,
                  }}>
                  <GripVertical size={14} style={{ color: 'var(--text-disabled)', flexShrink: 0 }} />
                  <TypeBadge type={c.type} small />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{c.nom}</p>
                    {c.requis && <p className="text-xs" style={{ color: '#DC2626' }}>Obligatoire</p>}
                  </div>
                  <button onClick={() => addCritere(c)}
                    data-testid={`add-critere-${c.critere_id}`}
                    className="p-1 rounded-lg hover:bg-blue-50 flex-shrink-0"
                    title="Ajouter au modèle">
                    <Plus size={14} style={{ color: 'var(--primary-solid)' }} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Col droite — Critères du modèle (drop zone + réordonnables) */}
          <div className="flex flex-col flex-1">
            <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                Modèle — {criteres.length} critère{criteres.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-disabled)' }}>
                Glissez des critères depuis le catalogue ou réordonnez-les
              </p>
            </div>
            <div
              className="flex-1 overflow-y-auto p-3 space-y-1.5"
              onDragOver={e => { if (draggingCatalogueId) { e.preventDefault(); } }}
              onDrop={e => { if (draggingCatalogueId) handleModelDrop(e); }}
              data-testid="modele-dropzone"
              style={{
                background: draggingCatalogueId ? '#EEF2FF' : 'transparent',
                transition: 'background 0.2s',
              }}>
              {criteres.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-3 py-16"
                  style={{ color: 'var(--text-disabled)' }}>
                  <GripVertical size={32} style={{ opacity: 0.3 }} />
                  <p className="text-sm font-medium">Glissez des critères ici</p>
                  <p className="text-xs text-center px-4">ou cliquez sur le + dans le catalogue</p>
                </div>
              ) : criteres.map((c, idx) => (
                <div
                  key={c.critere_id}
                  draggable
                  onDragStart={e => handleModelItemDragStart(e, idx)}
                  onDragOver={e => handleModelItemDragOver(e, idx)}
                  onDrop={e => handleModelItemDrop(e, idx)}
                  onDragEnd={() => { dragModelIdx.current = null; setDragOverIdx(null); }}
                  data-testid={`modele-critere-${c.critere_id}`}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-grab active:cursor-grabbing transition-all"
                  style={{
                    borderColor: dragOverIdx === idx ? 'var(--primary-solid)' : 'var(--border)',
                    background: dragOverIdx === idx ? '#EEF2FF' : 'white',
                    transform: dragOverIdx === idx ? 'scale(1.01)' : 'scale(1)',
                  }}>
                  <span className="flex-shrink-0 text-xs font-bold w-5 text-center" style={{ color: 'var(--text-disabled)' }}>
                    {idx + 1}
                  </span>
                  <GripVertical size={14} style={{ color: 'var(--text-disabled)', flexShrink: 0 }} />
                  <TypeBadge type={c.type} small />
                  <span className="flex-1 text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{c.nom}</span>
                  {c.requis && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
                      style={{ background: '#FEF2F2', color: '#DC2626' }}>Requis</span>
                  )}
                  <button onClick={() => removeCritere(c.critere_id)}
                    data-testid={`remove-critere-${c.critere_id}`}
                    className="p-1 rounded-lg hover:bg-red-50 flex-shrink-0">
                    <X size={13} style={{ color: '#EF4444' }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────

export default function CriteresControle() {
  const { activeAgences } = useSociete();
  const [activeTab, setActiveTab] = useState('bons_livraison');
  const [criteres, setCriteres] = useState([]);
  const [modeles, setModeles] = useState([]);
  const [bonsLivraison, setBonsLivraison] = useState([]);
  const [blStatutFilter, setBlStatutFilter] = useState('');
  const [blSearch, setBlSearch] = useState('');
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [pdfModal, setPdfModal] = useState(null); // blob URL
  const [loading, setLoading] = useState(true);

  // Catalogue state
  const [showNewCritere, setShowNewCritere] = useState(false);
  const [editCritere, setEditCritere] = useState(null);

  // Modèle state
  const [editModele, setEditModele] = useState(null); // null=closed, false=new, obj=edit

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, mRes, blRes] = await Promise.all([
        api.getCriteres(),
        api.getModelesControle(),
        api.listBonsLivraison(blStatutFilter ? { statut: blStatutFilter } : {}),
      ]);
      setCriteres(cRes.data?.items || []);
      setModeles(mRes.data?.items || []);
      setBonsLivraison(blRes.data?.items || []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  // activeAgences déclenche le rechargement quand l'utilisateur change d'agence
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blStatutFilter, activeAgences]);

  useEffect(() => { load(); }, [load]);

  const handleSaveCritere = async (payload) => {
    if (editCritere?.critere_id) {
      await api.updateCritere(editCritere.critere_id, payload);
      toast.success('Critère mis à jour');
    } else {
      await api.createCritere(payload);
      toast.success('Critère créé');
    }
    setShowNewCritere(false);
    setEditCritere(null);
    load();
  };

  const handleDeleteCritere = async (id) => {
    if (!window.confirm('Supprimer ce critère ?')) return;
    try {
      await api.deleteCritere(id);
      toast.success('Critère supprimé');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteModele = async (id) => {
    if (!window.confirm('Supprimer ce modèle ?')) return;
    try {
      await api.deleteModeleControle(id);
      toast.success('Modèle supprimé');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleDupliquerModele = async (id, nom) => {
    try {
      await api.dupliquerModeleControle(id);
      toast.success(`Modèle "Copie de ${nom}" créé`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleOpenPdf = async (bl) => {
    setPdfLoadingId(bl.bl_id);
    try {
      const res = await api.getBLPdf(bl.bl_id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfModal(url);
    } catch { toast.error('Erreur lors de la génération du PDF'); }
    finally { setPdfLoadingId(null); }
  };

  const closePdfModal = () => {
    if (pdfModal) URL.revokeObjectURL(pdfModal);
    setPdfModal(null);
  };

  return (
    <div className="flex flex-col h-full" style={{  }}>

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--text)' }}>BL avec contrôle</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Bons de livraison, modèles et catalogue de critères
          </p>
        </div>
        <button
          onClick={() => activeTab === 'modeles' ? setEditModele(false) : activeTab === 'catalogue' ? setShowNewCritere(true) : null}
          data-testid="btn-new"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
          style={{ background: activeTab === 'bons_livraison' ? 'transparent' : 'var(--primary-gradient)', display: activeTab === 'bons_livraison' ? 'none' : 'flex' }}>
          <Plus size={15} />
          {activeTab === 'modeles' ? 'Nouveau modèle' : 'Nouveau critère'}
        </button>
      </div>

      {/* Onglets */}
      <div className="flex border-b px-6" style={{ borderColor: 'var(--border)' }}>
        {[
          { id: 'bons_livraison', label: `Bons de livraison (${bonsLivraison.length})` },
          { id: 'modeles', label: `Modèles (${modeles.length})` },
          { id: 'catalogue', label: `Catalogue de critères (${criteres.length})` },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
            style={{
              borderBottomColor: activeTab === tab.id ? 'var(--primary-solid)' : 'transparent',
              color: activeTab === tab.id ? 'var(--primary-solid)' : 'var(--text-muted)',
              marginBottom: '-1px',
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
          </div>
        ) : activeTab === 'bons_livraison' ? (

          /* ── Onglet Bons de livraison ── */
          <div className="space-y-4">
            {/* Filtres statut + recherche */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                {[
                  { val: '', label: 'Tous' },
                  { val: 'en_attente', label: 'En attente' },
                  { val: 'signe', label: 'Signés' },
                ].map(f => (
                  <button key={f.val}
                    onClick={() => setBlStatutFilter(f.val)}
                    data-testid={`bl-filter-${f.val || 'all'}`}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all"
                    style={{
                      background: blStatutFilter === f.val ? 'var(--primary-50)' : 'white',
                      color: blStatutFilter === f.val ? 'var(--primary-solid)' : 'var(--text-muted)',
                      borderColor: blStatutFilter === f.val ? 'var(--primary-solid)' : 'var(--border)',
                    }}>
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="relative flex-1" style={{ minWidth: 200, maxWidth: 320 }}>
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-disabled)' }} />
                <input
                  value={blSearch}
                  onChange={e => setBlSearch(e.target.value)}
                  placeholder="Rechercher numéro, client, matériel..."
                  data-testid="bl-search-input"
                  className="w-full pl-8 pr-3 py-1.5 rounded-xl border text-sm"
                  style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}
                />
              </div>
            </div>

            {(() => {
              const q = blSearch.trim().toLowerCase();
              const filtered = bonsLivraison.filter(bl => {
                if (!q) return true;
                return [bl.numero, bl.contrat_numero, bl.client_nom, bl.materiel_nom, bl.gamme_nom, bl.signe_par]
                  .some(v => v && v.toLowerCase().includes(q));
              });
              if (filtered.length === 0) return (
                <div className="flex flex-col items-center justify-center py-20 gap-3"
                  style={{ color: 'var(--text-disabled)' }}>
                  <FileText size={40} style={{ opacity: 0.3 }} />
                  <p className="text-base font-medium">Aucun bon de livraison</p>
                  <p className="text-sm">Les BL sont créés automatiquement lors du clic sur "Livrer" dans un contrat</p>
                </div>
              );
              return (
                <div className="rounded-xl border overflow-x-auto" style={{ borderColor: 'var(--border)' }}>
                  <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                        {['Numéro', 'Contrat', 'Client', 'Matériel', 'Dates', 'Statut', 'Signé par', ''].map(h => (
                          <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap"
                            style={{ color: 'var(--text-muted)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(bl => (
                        <tr key={bl.bl_id}
                          data-testid={`bl-row-${bl.bl_id}`}
                          onClick={() => handleOpenPdf(bl)}
                          className="border-t hover:bg-blue-50 transition-colors cursor-pointer"
                          style={{ borderColor: 'var(--border)' }}>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>{bl.numero}</span>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className="text-sm" style={{ color: 'var(--text)' }}>{bl.contrat_numero}</span>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{bl.client_nom || '—'}</span>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <div>
                              <span className="text-sm" style={{ color: 'var(--text)' }}>
                                {[bl.materiel_marque, bl.materiel_modele].filter(Boolean).join(' ') || bl.materiel_nom || bl.gamme_nom || '—'}
                              </span>

                              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                {[
                                  bl.materiel_numero_parc && `Parc: ${bl.materiel_numero_parc}`,
                                  bl.materiel_immatriculation && `Immat: ${bl.materiel_immatriculation}`,
                                  bl.materiel_numero_serie && `S/N: ${bl.materiel_numero_serie}`,
                                ].filter(Boolean).join(' · ')}
                              </p>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              {bl.date_debut ? bl.date_debut.split('-').reverse().join('/') : '—'}
                              {bl.date_fin ? ` → ${bl.date_fin.split('-').reverse().join('/')}` : ''}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                              style={{
                                background: bl.statut === 'signe' ? '#F0FDF4' : '#FFFBEB',
                                color: bl.statut === 'signe' ? '#15803D' : '#D97706',
                              }}>
                              {bl.statut === 'signe' ? 'Signé' : 'En attente'}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            {bl.signe_par ? (
                              <div>
                                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{bl.signe_par}</span>
                                {bl.signed_at && (
                                  <p className="text-xs" style={{ color: 'var(--text-disabled)' }}>
                                    {bl.signed_at.split('T')[0].split('-').reverse().join('/')}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm" style={{ color: 'var(--text-disabled)' }}>—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {pdfLoadingId === bl.bl_id ? (
                              <Loader2 size={14} className="animate-spin mx-auto" style={{ color: 'var(--primary-solid)' }} />
                            ) : (
                              <Eye size={14} style={{ color: 'var(--text-disabled)' }} className="mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>

        ) : activeTab === 'modeles' ? (

          /* ── Onglet Modèles ── */
          <div className="space-y-4">
            {modeles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3"
                style={{ color: 'var(--text-disabled)' }}>
                <AlignJustify size={40} style={{ opacity: 0.3 }} />
                <p className="text-base font-medium">Aucun modèle de contrôle</p>
                <p className="text-sm">Créez votre premier modèle pour organiser vos critères</p>
                <button onClick={() => setEditModele(false)}
                  className="mt-2 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
                  style={{ background: 'var(--primary-gradient)' }}>
                  <Plus size={14} /> Nouveau modèle
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4" style={{ maxWidth: 900 }}>
                {modeles.map(m => (
                  <div key={m.modele_id}
                    data-testid={`modele-card-${m.modele_id}`}
                    className="rounded-xl border overflow-hidden hover:shadow-sm transition-shadow"
                    style={{ borderColor: 'var(--border)' }}>
                    {/* Header de la carte */}
                    <div className="flex items-center justify-between px-4 py-3 border-b"
                      style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
                      <div>
                        <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>{m.nom}</p>
                        {m.description && (
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{m.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                          {m.criteres?.length || 0} critère{(m.criteres?.length || 0) !== 1 ? 's' : ''}
                        </span>
                        <button onClick={() => handleDupliquerModele(m.modele_id, m.nom)}
                          data-testid={`dupliquer-modele-${m.modele_id}`}
                          title="Dupliquer ce modèle"
                          className="p-1.5 rounded-lg hover:bg-amber-50">
                          <Copy size={13} style={{ color: '#D97706' }} />
                        </button>
                        <button onClick={() => setEditModele(m)}
                          data-testid={`edit-modele-${m.modele_id}`}
                          className="p-1.5 rounded-lg hover:bg-blue-50">
                          <Edit2 size={13} style={{ color: 'var(--primary-solid)' }} />
                        </button>
                        <button onClick={() => handleDeleteModele(m.modele_id)}
                          data-testid={`delete-modele-${m.modele_id}`}
                          className="p-1.5 rounded-lg hover:bg-red-50">
                          <Trash2 size={13} style={{ color: '#EF4444' }} />
                        </button>
                      </div>
                    </div>
                    {/* Liste des critères */}
                    {m.criteres?.length > 0 && (
                      <div className="px-4 py-2 flex flex-wrap gap-1.5">
                        {m.criteres.map((c, i) => (
                          <span key={c.critere_id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs"
                            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'white' }}>
                            <span className="text-xs font-bold" style={{ color: 'var(--text-disabled)' }}>{i + 1}.</span>
                            <TypeBadge type={c.type} small />
                            {c.nom}
                            {c.requis && <span style={{ color: '#DC2626' }}>*</span>}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

        ) : (

          /* ── Onglet Catalogue de critères ── */
          <div className="space-y-4" style={{ maxWidth: 900 }}>
            {(showNewCritere || editCritere) && (
              <CritereForm
                initial={editCritere || null}
                onSave={handleSaveCritere}
                onCancel={() => { setShowNewCritere(false); setEditCritere(null); }}
              />
            )}
            {criteres.length === 0 && !showNewCritere ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3"
                style={{ color: 'var(--text-disabled)' }}>
                <CheckSquare size={40} style={{ opacity: 0.3 }} />
                <p className="text-base font-medium">Catalogue vide</p>
                <p className="text-sm">Créez vos premiers critères pour les utiliser dans vos modèles</p>
                <button onClick={() => setShowNewCritere(true)}
                  className="mt-2 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
                  style={{ background: 'var(--primary-gradient)' }}>
                  <Plus size={14} /> Nouveau critère
                </button>
              </div>
            ) : (
              <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                <table className="w-full">
                  <thead>
                    <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                      {['Type', 'Nom', 'Description', 'Détails', 'Requis', ''].map(h => (
                        <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wide"
                          style={{ color: 'var(--text-muted)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {criteres.map((c, i) => (
                      <tr key={c.critere_id}
                        data-testid={`critere-row-${c.critere_id}`}
                        className="border-t hover:bg-gray-50 transition-colors"
                        style={{ borderColor: 'var(--border)' }}>
                        <td className="px-4 py-3"><TypeBadge type={c.type} /></td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{c.nom}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{c.description || '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          {c.type === 'select' && c.options?.length > 0 && (
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{c.options.join(' / ')}</span>
                          )}
                          {c.type === 'niveau' && c.niveaux?.length > 0 && (
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{c.niveaux.join(' – ')}%</span>
                          )}
                          {c.type === 'compteur' && c.unite && (
                            <span className="text-xs px-2 py-0.5 rounded-full"
                              style={{ background: '#FAF5FF', color: '#7C3AED' }}>{c.unite}</span>
                          )}
                          {c.type === 'note' && (
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>★ / {c.note_max || 5}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {c.requis
                            ? <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: '#FEF2F2', color: '#DC2626' }}>Requis</span>
                            : <span className="text-xs" style={{ color: 'var(--text-disabled)' }}>—</span>
                          }
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <button onClick={() => { setEditCritere(c); setShowNewCritere(false); }}
                              data-testid={`edit-critere-${c.critere_id}`}
                              className="p-1.5 rounded-lg hover:bg-blue-50">
                              <Edit2 size={13} style={{ color: 'var(--primary-solid)' }} />
                            </button>
                            <button onClick={() => handleDeleteCritere(c.critere_id)}
                              data-testid={`delete-critere-${c.critere_id}`}
                              className="p-1.5 rounded-lg hover:bg-red-50">
                              <Trash2 size={13} style={{ color: '#EF4444' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Panel modèle (2 colonnes) */}
      {editModele !== null && (
        <ModelePanel
          modele={editModele || null}
          criteresCatalogue={criteres}
          onClose={() => setEditModele(null)}
          onSaved={load}
        />
      )}

      {/* Modal PDF BL */}
      {pdfModal && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="flex items-center justify-between px-4 py-2 flex-shrink-0"
            style={{ background: '#1A3A6E' }}>
            <span className="text-sm font-semibold text-white">Aperçu du bon de livraison</span>
            <button onClick={closePdfModal}
              data-testid="close-pdf-modal"
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
              <X size={18} className="text-white" />
            </button>
          </div>
          <iframe
            src={pdfModal}
            title="Aperçu BL"
            data-testid="pdf-iframe"
            className="flex-1 w-full border-0"
            style={{ background: '#525659' }}
          />
        </div>
      )}
    </div>
  );
}
